import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FontPicker, { toString, type Font, type FontToVariant } from "react-fontpicker-ts";
import "react-fontpicker-ts/dist/index.css";
import { localFonts } from "../../lib/schedule-art/local-fonts";
import { prepareGoogleArtFont } from "../../lib/schedule-art/art-fonts";
import { NativeSelect } from "../ui/native-select";

const noLocalFonts: Font[] = [];
const chineseFonts = (font: Font) => font.subsets?.includes("chinese-traditional") ?? false;

function normalWeights(font: FontToVariant) {
  const variants = font.variants.map(toString);
  const regular = variants.filter(variant => variant.startsWith("0,"));
  const weights = regular.map(variant => Number(variant.split(",")[1]));
  return [...new Set(weights)].sort((left, right) => left - right);
}

type GoogleFontPickerProps = {
  current: string;
  chineseOnly: boolean;
  onChange: (font: string, weights: number[]) => void;
};

function GoogleFontPicker({ current, chineseOnly, onChange }: GoogleFontPickerProps) {
  const { t } = useTranslation();
  const interacted = useRef(false);
  const variants = useRef<FontToVariant | null>(null);
  return (
    // Style from the wrapper: passing className to FontPicker replaces its positioning classes.
    // String.raw keeps the escaped underscores in the library's class names for Tailwind.
    <div className={String.raw`min-w-0 focus-within:pb-[min(14rem,40dvh)]
      [&_.fontpicker]:!relative [&_.fontpicker]:!h-8 [&_.fontpicker]:!w-full
      [&_.fontpicker]:!rounded-md [&_.fontpicker]:!border-neutral-300 [&_.fontpicker]:!bg-white
      [&_.fontpicker\_\_preview]:!filter-none [&_.fontpicker\_\_option]:!filter-none
      [&_.fontpicker\_\_preview]:!absolute [&_.fontpicker\_\_preview]:!top-1/2
      [&_.fontpicker\_\_preview]:!-translate-y-1/2 [&_.fontpicker\_\_preview]:!w-full
      [&_.fontpicker\_\_search]:!bg-white [&_.fontpicker\_\_search]:!text-neutral-900
      [&_.fontpicker\_\_search]:!m-0 [&_.fontpicker\_\_search]:!py-0 [&_.fontpicker\_\_search]:!leading-7 [&_.fontpicker\_\_search]:!text-xs
      [&_.fontpicker\_\_popout]:!z-20 [&_.fontpicker\_\_popout]:!bg-white
      [&_.fontpicker\_\_popout]:!max-h-[min(14rem,40dvh)] [&_.fontpicker\_\_popout]:overscroll-contain`}
      title={t("builder.font.searchHelp")}
      onPointerDownCapture={() => { interacted.current = true; }} onKeyDownCapture={() => { interacted.current = true; }}>
      <FontPicker defaultValue={current} googleFonts={chineseOnly ? chineseFonts : "all"} localFonts={noLocalFonts}
        autoLoad={false} noMatches={t("builder.font.noMatches")} loading={<p>{t("builder.font.loadingList")}</p>}
        fontVariants={font => { variants.current = font; }}
        value={font => {
          // The package emits its default on mount. Only a user's selection is an edit.
          if (!interacted.current) return;
          interacted.current = false;
          const info = variants.current;
          const weights = info?.fontName === font ? normalWeights(info) : [400];
          if (!weights.length) return;
          void prepareGoogleArtFont(font, weights);
          onChange(font, weights);
        }} />
    </div>
  );
}

type TextFontPickerProps = { font: string; onChange: (font: string, weights?: number[]) => void };

export function TextFontPicker({ font, onChange }: TextFontPickerProps) {
  const { t } = useTranslation();
  const bundled = localFonts.some(local => local.family === font) || font === "sans-serif";
  const [source, setSource] = useState(bundled ? "bundled" : "chinese");
  return (
    <div className="@container">
      <div className="grid items-start gap-2 @sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <label className="grid min-w-0 gap-1 text-xs font-medium">
          {t("builder.font.source")}
          <NativeSelect value={source} className="h-8 py-1 text-xs" onChange={event => setSource(event.target.value)}>
            <option value="chinese">{t("builder.font.chinese")}</option>
            <option value="google">{t("builder.font.google")}</option>
            <option value="bundled">{t("builder.font.bundled")}</option>
          </NativeSelect>
        </label>
        <div className="grid min-w-0 gap-1">
          <p className="text-xs font-medium" title={t("builder.font.selected", { font })}>{t("metadata.field.font")}</p>
          {source === "bundled" ? <NativeSelect value={bundled ? font : ""} className="h-8 py-1 text-xs" onChange={event => onChange(event.target.value)}>
            {!bundled && <option value="" disabled>{t("builder.font.chooseBundled")}</option>}
            <option value="sans-serif">{t("builder.font.default")}</option>
            {localFonts.map(local => <option key={local.family} value={local.family}>{local.family}</option>)}
          </NativeSelect> : <GoogleFontPicker key={`${source}:${font}`} current={font} chineseOnly={source === "chinese"} onChange={onChange} />}
        </div>
      </div>
    </div>
  );
}
